import React, { useState, useEffect, useCallback, useRef } from 'react';
import CollectionSearch from '../components/search/CollectionSearch';
import { filterRecords } from '../utils/search';
import {
  Container,
  Typography,
  Box,
  Paper,
  Chip,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Divider,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import BlockIcon from '@mui/icons-material/Block';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ComplianceProgress from '../components/compliance/ComplianceProgress';
import TransactionReviewCard from '../components/compliance/TransactionReviewCard';
import VendorRegistryView from '../components/compliance/VendorRegistryView';
import { needsDisposition } from '../components/compliance/dispositions';
import {
  getComplianceMonths,
  getComplianceMonth,
  reviewComplianceTransaction,
  uploadComplianceReceipt,
  uploadChaseStatement,
  generateCompliancePacket,
  signCompliancePacket,
  getCompliancePacketUrl,
  getComplianceVendors,
  requestDownloadCode,
  verifyDownloadCode,
  getDownloadAuthStatus,
  getAllowedDownloadEmails,
  emailCompliancePacket,
  emailDeductionSummary,
  updateComplianceVendor,
} from '../services/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress as MuiCircularProgress,
} from '@mui/material';

function CompliancePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Month tabs from API
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [monthsLoading, setMonthsLoading] = useState(true);
  const [monthsError, setMonthsError] = useState('');

  // Active month data from API
  const [monthData, setMonthData] = useState(null);
  const [monthLoading, setMonthLoading] = useState(false);
  const [monthError, setMonthError] = useState('');

  // UI state
  const [completeExpanded, setCompleteExpanded] = useState(false);
  const [search, setSearch] = useState('');
  const [excludedExpanded, setExcludedExpanded] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [generatingPacket, setGeneratingPacket] = useState(false);

  // Vendors list for assign-to-existing flow
  const [vendors, setVendors] = useState([]);

  // Download verification (2FA)
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifyEmails, setVerifyEmails] = useState([]);
  const [verifySelectedEmail, setVerifySelectedEmail] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyStep, setVerifyStep] = useState('email'); // 'email' or 'code'
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [verifyMaskedEmail, setVerifyMaskedEmail] = useState('');
  const [pendingDownload, setPendingDownload] = useState(null); // {month, signed}

  // Statement upload
  const statementFileRef = useRef(null);
  const [statementUploading, setStatementUploading] = useState(false);

  // ---- Load all months on mount ----
  useEffect(() => {
    const fetchMonths = async () => {
      setMonthsLoading(true);
      setMonthsError('');
      try {
        const res = await getComplianceMonths();
        const data = res.data?.data || res.data || [];
        setMonths(data);
        // Default to the latest open month, or last month in list
        if (data.length > 0) {
          const openMonth = data.find((m) => m.status === 'open');
          setSelectedMonth(openMonth ? openMonth.month : data[data.length - 1].month);
        }
        // Also fetch vendors
        try {
          const vendorRes = await getComplianceVendors();
          setVendors(vendorRes.data?.data || vendorRes.data || []);
        } catch (e) {
          console.error('Failed to fetch vendors', e);
        }
      } catch (err) {
        console.error('Failed to fetch compliance months', err);
        setMonthsError(
          err.response?.data?.msg || 'Failed to load compliance months. The compliance tables may not exist yet.'
        );
      } finally {
        setMonthsLoading(false);
      }
    };
    fetchMonths();
  }, []);

  // ---- Load selected month data ----
  const fetchMonthData = useCallback(async (month) => {
    if (!month) return;
    setMonthLoading(true);
    setMonthError('');
    try {
      const res = await getComplianceMonth(month);
      const data = res.data?.data || res.data || {};
      setMonthData(data);
    } catch (err) {
      console.error('Failed to fetch month data', err);
      setMonthError(
        err.response?.data?.msg || 'Failed to load month data. This may be the first access — Teller pull in progress.'
      );
      setMonthData(null);
    } finally {
      setMonthLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      fetchMonthData(selectedMonth);
    }
  }, [selectedMonth, fetchMonthData]);

  // ---- Derived data from API response ----
  const packet = monthData?.packet || null;
  const transactions = monthData?.transactions || [];
  const statements = monthData?.statements || [];
  const completeness = monthData?.completeness || null;

  const isOpenMonth = packet?.status === 'open';
  const isSignedMonth = packet?.status === 'signed';
  const isCompleteMonth = packet?.status === 'complete';

  // Categorize transactions
  const orphanedTransactions = transactions.filter((t) => t.is_orphaned);
  const pendingTransactions = transactions.filter((t) => needsDisposition(t) && !t.is_orphaned);
  const confirmedMissingReceipt = transactions.filter(
    (t) =>
      t.disposition === 'claim' &&
      t.review_status !== 'pending' &&
      t.receipt_status === 'none' &&
      t.receipt_required !== false &&
      !t.is_orphaned
  );
  const confirmedWithReceipt = transactions.filter(
    (t) =>
      t.disposition === 'claim' &&
      t.review_status !== 'pending' &&
      (t.receipt_status === 'uploaded' || t.receipt_required === false) &&
      !t.is_orphaned
  );
  const excludedTransactions = transactions.filter(
    (t) => t.disposition === 'exclude' || t.disposition === 'void'
  );
  const visibleTransactions = filterRecords(transactions, search, transaction => [
    transaction.vendor_name, transaction.description, transaction.category, transaction.amount, transaction.date,
    transaction.business_purpose_brief, transaction.business_purpose_formal, transaction.disposition,
    transaction.review_status, transaction.receipt_filename,
    transaction.receipt_status === 'none' ? 'missing receipt' : transaction.receipt_status,
  ]);
  const visibleIds = new Set(visibleTransactions.map(transaction => transaction.id));
  // A close text match must never outrank a more recent charge within its section.
  const shown = rows => rows.filter(transaction => visibleIds.has(transaction.id))
    .sort((left, right) => (right.date || '').localeCompare(left.date || ''));
  const dated = rows => rows.slice().sort((left, right) => (right.date || '').localeCompare(left.date || ''));

  // Statement availability check
  // The 2nd statement (next month's 13th) isn't available until that date
  const [selYear, selMo] = (selectedMonth || '2026-01').split('-').map(Number);
  const nextMonth13 = new Date(selMo === 12 ? selYear + 1 : selYear, selMo === 12 ? 0 : selMo, 13);
  const secondStatementAvailable = new Date() >= nextMonth13;
  const statementsExpected = secondStatementAvailable ? 2 : 1;
  const missingStatements = Math.max(0, statementsExpected - statements.length);

  // Needs attention items (includes actionable missing statements)
  const needsAttentionCount =
    pendingTransactions.length +
    confirmedMissingReceipt.length +
    missingStatements;

  // Progress data for ComplianceProgress component
  const allClaimedTxns = transactions.filter((t) => t.disposition === 'claim' && t.review_status !== 'pending');
  const receiptUploadedCount = allClaimedTxns.filter(
    (t) => t.receipt_status === 'uploaded' || t.receipt_required === false
  ).length;

  // Statement pending message
  const statementPendingMsg = (!secondStatementAvailable && statements.length >= 1)
    ? 'Chase statement pending (available ' + nextMonth13.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ')'
    : null;

  const progressData = {
    receiptUploaded: receiptUploadedCount,
    receiptTotal: allClaimedTxns.length,
    statementUploaded: statements.length,
    statementTotal: 2,  // Always show out of 2 so user knows 2 are needed
    needsAttentionCount,
    status: packet?.status || 'open',
    statementPendingMsg,
  };

  // ---- Handlers ----
  const handleMonthChange = (event, newValue) => {
    setSelectedMonth(newValue);
    setSearch('');
  };

  const handleTransactionSave = async (updatedTransaction) => {
    try {
      // 1. Save the review data
      await reviewComplianceTransaction(updatedTransaction.id, {
        vendor_name: updatedTransaction.vendor_name,
        vendor_id: updatedTransaction.vendor_id || null,
        vendor_slug: updatedTransaction.vendor_slug || '',
        category: updatedTransaction.category,
        business_pct: updatedTransaction.business_pct,
        pct_rationale: updatedTransaction.pct_rationale,
        business_purpose_brief: updatedTransaction.business_purpose_brief,
        business_purpose_formal: updatedTransaction.business_purpose_formal,
        registry_action: updatedTransaction.registry_action || 'none',
        disposition: updatedTransaction.disposition,
        exclusion_reason: updatedTransaction.exclusion_reason,
        learn_pattern: updatedTransaction.learn_pattern || null,
        retrieval_steps: updatedTransaction.retrieval_steps || null,
        receipt_analysis: updatedTransaction.receipt_analysis || null,
      });

      // 2. Upload receipt if a file was selected
      if (updatedTransaction._receiptFile) {
        const formData = new FormData();
        formData.append('file', updatedTransaction._receiptFile);
        await uploadComplianceReceipt(updatedTransaction.id, formData);
      }

      setSnackbar({
        open: true,
        message: `${updatedTransaction.vendor_name} saved and confirmed`,
        severity: 'success',
      });
      // Refresh month data to reflect changes
      fetchMonthData(selectedMonth);
    } catch (err) {
      console.error('Failed to save transaction review', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.msg || 'Failed to save transaction review',
        severity: 'error',
      });
      throw err; // The card must retain its draft rather than reporting false success.
    }
  };

  const handleGeneratePacket = async () => {
    setGeneratingPacket(true);
    try {
      await generateCompliancePacket(selectedMonth);
      setSnackbar({
        open: true,
        message: 'Compliance packet generated successfully',
        severity: 'success',
      });
      // Refresh to get updated status
      fetchMonthData(selectedMonth);
    } catch (err) {
      console.error('Failed to generate packet', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.msg || 'Failed to generate compliance packet',
        severity: 'error',
      });
    } finally {
      setGeneratingPacket(false);
    }
  };

  const handleSignPacket = async () => {
    try {
      await signCompliancePacket(selectedMonth);
      setSnackbar({
        open: true,
        message: 'Packet signed and finalized',
        severity: 'success',
      });
      fetchMonthData(selectedMonth);
    } catch (err) {
      console.error('Failed to sign packet', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.msg || 'Failed to sign packet',
        severity: 'error',
      });
    }
  };

  const handleEmailDocument = async (month, docType, options = {}) => {
    // docType: 'packet' or 'summary'
    // options: { signed, format, email }
    const email = options.email || verifySelectedEmail;

    try {
      let res;
      if (docType === 'packet') {
        res = await emailCompliancePacket(month, email, options.signed !== false);
      } else {
        res = await emailDeductionSummary(month, email, options.format || 'xlsx');
      }
      setSnackbar({
        open: true,
        message: res.data?.msg || 'Document sent to ' + email,
        severity: 'success',
      });
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.requires_verification) {
        setPendingDownload({ month, docType, ...options });
        try {
          const emailRes = await getAllowedDownloadEmails();
          setVerifyEmails(emailRes.data?.data?.emails || []);
        } catch (e) {
          setVerifyEmails(['mna9@cornell.edu', 'mike@intellisoftventures.com', 'mike@fitnesslog.org']);
        }
        setVerifyStep('email');
        setVerifyCode('');
        setVerifyError('');
        setVerifySelectedEmail('');
        setVerifyModalOpen(true);
        return;
      }
      setSnackbar({
        open: true,
        message: err.response?.data?.msg || 'Failed to send document',
        severity: 'error',
      });
    }
  };

  const handleRequestCode = async () => {
    if (!verifySelectedEmail) return;
    setVerifyLoading(true);
    setVerifyError('');
    try {
      const res = await requestDownloadCode(verifySelectedEmail);
      if (res.data?.verified) {
        // Already have active session — just send
        setVerifyModalOpen(false);
        if (pendingDownload) {
          handleEmailDocument(
            pendingDownload.month,
            pendingDownload.docType || 'packet',
            { ...pendingDownload, email: verifySelectedEmail }
          );
        }
        return;
      }
      setVerifyMaskedEmail(res.data?.masked_email || verifySelectedEmail);
      setVerifyStep('code');
    } catch (err) {
      setVerifyError(err.response?.data?.msg || 'Failed to send code');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verifyCode || verifyCode.length !== 6) return;
    setVerifyLoading(true);
    setVerifyError('');
    try {
      await verifyDownloadCode(verifyCode);
      setVerifyModalOpen(false);
      setSnackbar({
        open: true,
        message: 'Verified — document delivery authorized for 5 minutes',
        severity: 'success',
      });
      // Retry the pending download
      if (pendingDownload) {
        if (pendingDownload) {
          setTimeout(() => {
            handleEmailDocument(
              pendingDownload.month,
              pendingDownload.docType || 'packet',
              { ...pendingDownload, email: verifySelectedEmail }
            );
          }, 500);
        }
      }
    } catch (err) {
      setVerifyError(err.response?.data?.msg || 'Invalid code');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleStatementUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Determine statement closing date — Chase closes on the 13th
    // Each month needs two statements: 13th of that month + 13th of next month
    const [year, mo] = selectedMonth.split('-').map(Number);
    const thisMonth13 = `${selectedMonth}-13`;
    const nextMo = mo === 12 ? 1 : mo + 1;
    const nextYear = mo === 12 ? year + 1 : year;
    const nextMonth13 = `${nextYear}-${String(nextMo).padStart(2, '0')}-13`;

    const dateChoice = window.prompt(
      `Which statement closing date is this?\n\n1) ${thisMonth13}\n2) ${nextMonth13}\n\nEnter 1 or 2:`
    );
    let statementDate;
    if (dateChoice === '1') statementDate = thisMonth13;
    else if (dateChoice === '2') statementDate = nextMonth13;
    else {
      if (statementFileRef.current) statementFileRef.current.value = '';
      return;
    }

    setStatementUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('statement_date', statementDate);
      await uploadChaseStatement(selectedMonth, formData);
      setSnackbar({
        open: true,
        message: 'Chase statement uploaded successfully',
        severity: 'success',
      });
      fetchMonthData(selectedMonth);
    } catch (err) {
      console.error('Failed to upload statement', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.msg || 'Failed to upload Chase statement',
        severity: 'error',
      });
    } finally {
      setStatementUploading(false);
      // Reset file input
      if (statementFileRef.current) {
        statementFileRef.current.value = '';
      }
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null) return '$0.00';
    return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatMonthLabel = (monthStr) => {
    if (!monthStr) return '';
    const [year, mon] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(mon) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const formatMonthShort = (monthStr) => {
    if (!monthStr) return '';
    const [year, mon] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(mon) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'short' });
  };

  const formatStatementDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // ---- Loading / Error states ----
  if (monthsLoading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading compliance data...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (monthsError) {
    return (
      <Container sx={{ mt: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
        <Box sx={{ my: { xs: 2, md: 4 } }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Compliance Dashboard
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {monthsError}
          </Alert>
        </Box>
      </Container>
    );
  }

  if (months.length === 0) {
    return (
      <Container sx={{ mt: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
        <Box sx={{ my: { xs: 2, md: 4 } }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Compliance Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            FitnessLog LLC — Chase Business Card Expense Documentation
          </Typography>
          <Alert severity="info">
            No compliance months found. Transaction data will appear here once the compliance engine processes your
            Chase card transactions.
          </Alert>
        </Box>
      </Container>
    );
  }

  // Find the packet info for signed months from the months list
  const selectedMonthInfo = months.find((m) => m.month === selectedMonth);

  return (
    <Container sx={{ mt: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 3 }, mb: 4 }}>
      <Box sx={{ my: { xs: 2, md: 4 } }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Compliance Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          FitnessLog LLC — Chase Business Card Expense Documentation
        </Typography>

        {/* Month Selector Tabs + Vendors */}
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Tabs
                value={selectedMonth === '__vendors__' ? false : (selectedMonth || false)}
                onChange={handleMonthChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                {months.map((m) => (
                  <Tab
                    key={m.month}
                    value={m.month}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {formatMonthShort(m.month)}
                        {m.status === 'signed' && (
                          <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        )}
                        {m.status === 'open' && (
                          <Chip label="Active" size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                        {m.status === 'complete' && (
                          <Chip label="Ready" size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    }
                  />
                ))}
              </Tabs>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Button
              size="small"
              onClick={() => setSelectedMonth('__vendors__')}
              sx={{
                mr: 1,
                minWidth: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              <Chip
                label="Recurring Vendors"
                size="small"
                color={selectedMonth === '__vendors__' ? 'success' : 'default'}
                variant={selectedMonth === '__vendors__' ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer', fontWeight: selectedMonth === '__vendors__' ? 700 : 400 }}
              />
            </Button>
          </Box>
        </Paper>

        {/* ===== VENDOR REGISTRY VIEW ===== */}
        {selectedMonth === '__vendors__' && (
          <VendorRegistryView vendors={vendors} onUpdate={async (id, data) => {
            try {
              await updateComplianceVendor(id, data);
              // Refresh vendors
              const vendorRes = await getComplianceVendors();
              setVendors(vendorRes.data?.data || vendorRes.data || []);
              setSnackbar({ open: true, message: 'Vendor updated', severity: 'success' });
            } catch (err) {
              setSnackbar({ open: true, message: err.response?.data?.msg || 'Update failed', severity: 'error' });
            }
          }} />
        )}

        {/* Loading state for month data */}
        {selectedMonth !== '__vendors__' && monthLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading {formatMonthLabel(selectedMonth)} data...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Error state for month data */}
        {selectedMonth !== '__vendors__' && !monthLoading && monthError && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {monthError}
          </Alert>
        )}

        {/* ===== SIGNED MONTH VIEW ===== */}
        {selectedMonth !== '__vendors__' && !monthLoading && !monthError && (isSignedMonth || isCompleteMonth) && packet && (
          <Paper sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CheckCircleIcon color="success" />
              <Typography variant="h6">
                {formatMonthLabel(selectedMonth)} — {isSignedMonth ? 'Complete' : 'Generated'}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Charges
                </Typography>
                <Typography variant="h6">{packet.charge_count || 0}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Total Charged
                </Typography>
                <Typography variant="h6">{formatCurrency(packet.total_charges)}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Deductible
                </Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(packet.total_deductible)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={isSignedMonth ? 'Signed' : 'Generated'}
                  color="success"
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {isCompleteMonth && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSignPacket}
                >
                  Sign & Finalize
                </Button>
              )}
              {isCompleteMonth && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleGeneratePacket}
                  disabled={generatingPacket}
                >
                  {generatingPacket ? 'Regenerating...' : 'Regenerate'}
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={() => handleEmailDocument(selectedMonth, 'packet', { signed: false })}
              >
                Email Packet
              </Button>
              {isSignedMonth && (
                <Button
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={() => handleEmailDocument(selectedMonth, 'packet', { signed: true })}
                >
                  Email Signed
                </Button>
              )}
              <Button
                variant="outlined"
                color="success"
                startIcon={<EmailIcon />}
                onClick={() => handleEmailDocument(selectedMonth, 'summary', { format: 'xlsx' })}
              >
                Email Summary (Excel)
              </Button>
              <Button
                variant="outlined"
                color="success"
                startIcon={<EmailIcon />}
                onClick={() => handleEmailDocument(selectedMonth, 'summary', { format: 'csv' })}
                size="small"
              >
                CSV
              </Button>
            </Box>
          </Paper>
        )}

        {/* ===== ACTIVE (OPEN) MONTH VIEW ===== */}
        {selectedMonth !== '__vendors__' && !monthLoading && !monthError && isOpenMonth && packet && (
          <>
            {/* Progress */}
            <ComplianceProgress
              monthData={progressData}
              onGeneratePacket={handleGeneratePacket}
              onSignPacket={handleSignPacket}
              generating={generatingPacket}
            />

            {/* ---- NEEDS ATTENTION ---- */}
            <CollectionSearch label="Search monthly compliance transactions" value={search} onChange={setSearch}
              total={transactions.length} shown={visibleTransactions.length} />
            {search && !visibleTransactions.length && <Typography sx={{ mb: 2 }}>No matching transactions in this month.</Typography>}
            {(pendingTransactions.length > 0 || confirmedMissingReceipt.length > 0 || orphanedTransactions.length > 0) && (
              <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderLeft: 4, borderColor: 'warning.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <WarningIcon color="warning" />
                  <Typography variant="h6">
                    Needs Attention ({pendingTransactions.length + confirmedMissingReceipt.length + orphanedTransactions.length})
                  </Typography>
                </Box>

                {/* Orphaned transactions (pending replaced by posted) */}
                {orphanedTransactions.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="error.main" sx={{ mb: 1 }}>
                      Replaced Charges ({orphanedTransactions.length}) — may be old pending holds
                    </Typography>
                    {shown(orphanedTransactions).map((txn) => (
                      <Alert
                        key={txn.id}
                        severity="warning"
                        sx={{ mb: 1 }}
                        action={
                          <Button
                            color="inherit"
                            size="small"
                            onClick={async () => {
                              try {
                                const { deleteComplianceTransaction } = await import('../services/api');
                                await deleteComplianceTransaction(txn.id);
                                fetchMonthData(selectedMonth);
                                setSnackbar({ open: true, message: 'Orphaned charge deleted', severity: 'success' });
                              } catch (err) {
                                setSnackbar({ open: true, message: 'Failed to delete', severity: 'error' });
                              }
                            }}
                          >
                            Delete
                          </Button>
                        }
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {txn.vendor_name || txn.description} — ${Number(txn.amount).toFixed(2)} — {txn.date}
                        </Typography>
                        <Typography variant="caption">
                          This charge lost its link to the bank transaction — likely a pending hold that was replaced by the final posted amount. Delete if the correct charge exists below.
                        </Typography>
                      </Alert>
                    ))}
                  </Box>
                )}

                {/* New vendors */}
                {pendingTransactions.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="error.main" sx={{ mb: 1 }}>
                      New Vendors ({pendingTransactions.length})
                    </Typography>
                    {dated(pendingTransactions).map((txn) => (
                      <Box key={txn.id} hidden={!visibleIds.has(txn.id)}>
                      <TransactionReviewCard
                        key={txn.id}
                        transaction={txn}
                        onSave={handleTransactionSave}
                        vendors={vendors}
                      />
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Missing receipts */}
                {confirmedMissingReceipt.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1 }}>
                      Missing Receipts ({confirmedMissingReceipt.length})
                    </Typography>
                    {dated(confirmedMissingReceipt).map((txn) => (
                      <Box key={txn.id} hidden={!visibleIds.has(txn.id)}>
                      <TransactionReviewCard
                        key={txn.id}
                        transaction={txn}
                        onSave={handleTransactionSave}
                        vendors={vendors}
                      />
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            )}

            {/* ---- COMPLETE ---- */}
            {confirmedWithReceipt.length > 0 && (
              <Accordion
                expanded={completeExpanded || Boolean(search.trim())}
                onChange={(e, expanded) => setCompleteExpanded(expanded)}
                sx={{ mb: 3 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color="success" />
                    <Typography variant="h6">
                      Complete ({confirmedWithReceipt.length})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {dated(confirmedWithReceipt).map((txn) => (
                    <Box key={txn.id} hidden={!visibleIds.has(txn.id)}>
                    <TransactionReviewCard
                      key={txn.id}
                      transaction={txn}
                      onSave={handleTransactionSave}
                      vendors={vendors}
                    />
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}

            {/* ---- EXCLUDED ---- */}
            {excludedTransactions.length > 0 && (
              <Accordion
                expanded={excludedExpanded || Boolean(search.trim())}
                onChange={(e, expanded) => setExcludedExpanded(expanded)}
                sx={{ mb: 3 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BlockIcon color="action" />
                    <Typography variant="h6">
                      Excluded & Voided ({excludedTransactions.length})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {dated(excludedTransactions).map((txn) => (
                    <Box key={txn.id} hidden={!visibleIds.has(txn.id)}>
                    <TransactionReviewCard
                      key={txn.id}
                      transaction={txn}
                      onSave={handleTransactionSave}
                      vendors={vendors}
                    />
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}

            {/* ---- CHASE STATEMENTS ---- */}
            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Chase Statements
              </Typography>
              {statements.length > 0 ? (
                statements.map((stmt) => (
                  <Paper
                    key={stmt.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DescriptionIcon color="success" />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Statement closing {formatStatementDate(stmt.statement_date)}
                        </Typography>
                        {stmt.original_filename && (
                          <Typography variant="caption" color="text.secondary">
                            {stmt.original_filename}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Chip icon={<CheckCircleIcon />} label="Uploaded" color="success" size="small" />
                  </Paper>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No statements uploaded yet for this period.
                </Typography>
              )}

              {/* Statement upload button */}
              <Box sx={{ mt: 2 }}>
                <input
                  type="file"
                  ref={statementFileRef}
                  onChange={handleStatementUpload}
                  accept=".pdf"
                  style={{ display: 'none' }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    statementUploading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <UploadFileIcon />
                    )
                  }
                  onClick={() => statementFileRef.current?.click()}
                  disabled={statementUploading}
                >
                  {statementUploading ? 'Uploading...' : 'Upload Chase Statement'}
                </Button>
              </Box>
            </Paper>

            {/* ---- SUMMARY STATS ---- */}
            {packet && (
              <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Month Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Total Charges</Typography>
                    <Typography variant="h6">{formatCurrency(packet.total_charges)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Deductible</Typography>
                    <Typography variant="h6" color="success.main">{formatCurrency(packet.total_deductible)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Excluded</Typography>
                    <Typography variant="h6" color="text.secondary">{formatCurrency(packet.total_excluded)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">Transactions</Typography>
                    <Typography variant="h6">
                      {packet.claimed_count || 0} claimed / {packet.excluded_count || 0} excluded
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* ---- COMPLETED PACKETS (other months) ---- */}
            {months.filter((m) => m.status === 'signed' || m.status === 'complete').length > 0 && (
              <Paper sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Completed Packets
                </Typography>
                {months
                  .filter((m) => m.status === 'signed' || m.status === 'complete')
                  .map((pkt) => (
                    <Paper
                      key={pkt.month}
                      variant="outlined"
                      sx={{ p: 2, mb: 1 }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {formatMonthLabel(pkt.month)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {pkt.charge_count || 0} charges — {formatCurrency(pkt.total_deductible)} deductible
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            icon={<CheckCircleIcon />}
                            label={pkt.status === 'signed' ? 'Signed' : 'Generated'}
                            color={pkt.status === 'signed' ? 'success' : 'info'}
                            size="small"
                          />
                          {pkt.status === 'complete' && (
                            <Button
                              variant="contained"
                              size="small"
                              color="success"
                              onClick={async () => {
                                try {
                                  await signCompliancePacket(pkt.month);
                                  setSnackbar({ open: true, message: 'Packet signed and finalized', severity: 'success' });
                                  const res = await getComplianceMonths();
                                  setMonths(res.data?.data || res.data || []);
                                } catch (err) {
                                  setSnackbar({ open: true, message: err.response?.data?.msg || 'Failed to sign', severity: 'error' });
                                }
                              }}
                            >
                              Sign & Finalize
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EmailIcon />}
                            onClick={() => handleEmailDocument(pkt.month, 'packet', { signed: false })}
                          >
                            Packet
                          </Button>
                          {pkt.status === 'signed' && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<EmailIcon />}
                              onClick={() => handleEmailDocument(pkt.month, 'packet', { signed: true })}
                            >
                              Signed
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            size="small"
                            color="success"
                            startIcon={<EmailIcon />}
                            onClick={() => handleEmailDocument(pkt.month, 'summary', { format: 'xlsx' })}
                          >
                            Excel
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
              </Paper>
            )}
          </>
        )}
      </Box>

      {/* Download Verification Modal */}
      <Dialog open={verifyModalOpen} onClose={() => setVerifyModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Secure Document Delivery</DialogTitle>
        <DialogContent>
          {verifyStep === 'email' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                For security, a verification code will be sent to your email. Once verified, documents will be emailed to your chosen address for 5 minutes.
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Send code to</InputLabel>
                <Select
                  value={verifySelectedEmail}
                  onChange={(e) => setVerifySelectedEmail(e.target.value)}
                  label="Send code to"
                >
                  {verifyEmails.map((email) => (
                    <MenuItem key={email} value={email}>{email}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {verifyError && <Alert severity="error">{verifyError}</Alert>}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Code sent to <strong>{verifyMaskedEmail}</strong>. Enter it below.
              </Typography>
              <MuiTextField
                label="6-digit code"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                fullWidth
                size="small"
                autoFocus
                inputProps={{ maxLength: 6, style: { letterSpacing: '8px', fontSize: '24px', textAlign: 'center' } }}
                onKeyDown={(e) => { if (e.key === 'Enter' && verifyCode.length === 6) handleVerifyCode(); }}
              />
              {verifyError && <Alert severity="error">{verifyError}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyModalOpen(false)}>Cancel</Button>
          {verifyStep === 'email' ? (
            <Button
              variant="contained"
              onClick={handleRequestCode}
              disabled={!verifySelectedEmail || verifyLoading}
              startIcon={verifyLoading ? <MuiCircularProgress size={16} color="inherit" /> : null}
            >
              {verifyLoading ? 'Sending...' : 'Send Code'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleVerifyCode}
              disabled={verifyCode.length !== 6 || verifyLoading}
              startIcon={verifyLoading ? <MuiCircularProgress size={16} color="inherit" /> : null}
            >
              {verifyLoading ? 'Verifying...' : 'Verify'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default CompliancePage;
